import { v4 as uuid } from "uuid";
import type { SpiceLevel } from "@/features/sem-generator/util/constants";
import type { BetSelection, CombinationStake } from "@/features/betslip/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type BetPayloadSelection = {
  // Exotic legs go out as the string "1.00". The betting service rejects
  // numeric 0/1 for exotics.
  odds: number | string;
  outcomeIds: string[];
  selectionType?: "Exotic" | "Quaddie";
  correlationId?: string;
  attributes?: { originType: string; originId?: string };
};

type BetPayload = {
  stake: number;
  correlationId: string;
  stakeSource: "AVAILABLE" | "PROMOTION_USER";
  // Legacy omits these keys on singles/combos when there is no value and
  // only sends them (nullable) on the multi bet — mirror that shape.
  tokenIdAttemptedToClaim?: string | null;
  alternativeStakeReference?: string | null;
  selections: BetPayloadSelection[];
  attributes?: { originType: string; originId?: string };
  exoticCombinations?: number;
  quaddieMarketId?: string;
  selectionsRequiredToWin?: number;
  multiBuilderType?: "MYSTERY";
  mysteryMultiLevel?: SpiceLevel;
};

export type SubmissionPayload = {
  currency: string;
  acceptOddsChanges: string;
  bets: BetPayload[];
  channel: string;
};

export type BuildPayloadParams = {
  selections: Record<string, BetSelection>;
  currency: string;
  acceptOddsChanges: string;
  channel: string;

  // Singles
  singleStakes?: Record<string, number>;
  /** Per-bet "use promo cash" flag. */
  singlePromoCash?: Record<string, boolean>;
  /** Per-bet token allocation id (or null). */
  singleTokens?: Record<string, string | null>;

  // Multi
  multiStake?: number;
  multiPromoCash?: boolean;
  multiToken?: string | null;
  multiLegs?: BetSelection[];

  // Combinations
  combinations?: Record<string, CombinationStake>;

  // Alternative-stake references from a previous rejection. Re-attached
  // on re-submit so the backend treats them as the same bet attempt.
  alternativeStakeReferences?: Record<string, string | null>;
  multiAlternativeStakeReference?: string | null;

  // Pre-generated correlationIds for multi legs. The caller passes these
  // so rejection responses with
  // `propertyPath: "selections.<correlationId>"` can be routed back to
  // the originating outcomeId.
  multiLegCorrelationIds?: Record<string, string>;

  // Pre-generated bet-level correlationIds for single bets, keyed by the
  // slip selection key. Responses whose echoed outcomeIds can't be matched
  // to a selection (e.g. quaddies) are routed back via these ids.
  singleCorrelationIds?: Record<string, string>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the outcome IDs a selection submits to the betting service.
 * Exported so response routing can match echoed outcomeIds back to the
 * originating slip selection with the same logic.
 *
 * Exotics submit the real outcome id of every selected runner-position,
 * deduped because quinella (RUNNER_TOP_2) duplicates each runner into
 * positions 1 and 2. The selection's own `outcomeId` is a slip-side key,
 * not a real outcome.
 */
export const getSubmittedOutcomeIds = (selection: BetSelection): string[] => {
  if (selection.kind === "sem") {
    return selection.legs.map((l) => l.outcomeId);
  }
  if (selection.kind === "exotic") {
    return [
      ...new Set(selection.positions.flat().map((entry) => entry.outcomeId)),
    ];
  }
  if (selection.kind === "quaddie") {
    return Object.values(selection.quaddieSelections ?? {}).flatMap((runners) =>
      runners.map((runner) => runner.outcomeId),
    );
  }
  return [selection.outcomeId];
};

/** Return the selectionType the backend expects for exotic/quaddie bets. */
const getSelectionType = (
  selection: BetSelection,
): "Exotic" | "Quaddie" | undefined => {
  if (selection.kind === "exotic") return "Exotic";
  if (selection.kind === "quaddie") return "Quaddie";
  return undefined;
};

// ---------------------------------------------------------------------------
// buildPayload
// ---------------------------------------------------------------------------

/**
 * Build the submission payload from betslip state. Pure function.
 */
export const buildPayload = (params: BuildPayloadParams): SubmissionPayload => {
  const {
    selections,
    currency,
    acceptOddsChanges,
    channel,
    singleStakes = {},
    singlePromoCash = {},
    singleTokens = {},
    multiStake = 0,
    multiPromoCash = false,
    multiToken = null,
    multiLegs = [],
    combinations = {},
    alternativeStakeReferences = {},
    multiAlternativeStakeReference = null,
    multiLegCorrelationIds = {},
    singleCorrelationIds = {},
  } = params;

  const bets: BetPayload[] = [];

  // --- Singles ---
  for (const [outcomeId, selection] of Object.entries(selections)) {
    const stake = singleStakes[outcomeId] ?? 0;
    if (stake <= 0) continue;

    const isPromo = singlePromoCash[outcomeId] ?? false;
    const tokenId = singleTokens[outcomeId] ?? null;

    // Quaddies submit one selection per race, each carrying that race's
    // selected runner outcomeIds. That's the shape the betting service
    // expects.
    const innerSelections: BetPayloadSelection[] =
      selection.kind === "quaddie"
        ? Object.values(selection.quaddieSelections ?? {}).map((runners) => ({
            odds: 1,
            outcomeIds: runners.map((r) => r.outcomeId),
            selectionType: "Quaddie",
          }))
        : [
            {
              odds: selection.kind === "exotic" ? "1.00" : selection.odds,
              outcomeIds: getSubmittedOutcomeIds(selection),
              selectionType: getSelectionType(selection),
            },
          ];

    const alternativeStakeReference = alternativeStakeReferences[outcomeId];

    bets.push({
      stake,
      correlationId: singleCorrelationIds[outcomeId] ?? uuid(),
      stakeSource: isPromo ? "PROMOTION_USER" : "AVAILABLE",
      // Tokens are claimed independently of promo cash — promo is a
      // stake funding source, tokens are their own feature.
      ...(tokenId != null && { tokenIdAttemptedToClaim: tokenId }),
      ...(alternativeStakeReference != null && { alternativeStakeReference }),
      selections: innerSelections,
      attributes: {
        originType: selection.originType,
        originId: selection.originId,
      },
      ...(selection.kind === "exotic" && {
        exoticCombinations: selection.combinations,
      }),
      ...(selection.kind === "quaddie" && {
        quaddieMarketId: selection.marketId,
      }),
      ...(selection.kind === "sem" &&
        selection.mysteryMulti && {
          multiBuilderType: "MYSTERY" as const,
          ...(selection.mysteryMulti.level != null && {
            mysteryMultiLevel: selection.mysteryMulti.level,
          }),
        }),
    });
  }

  // --- Multi ---
  if (multiStake > 0 && multiLegs.length >= 2) {
    const isPromo = multiPromoCash;

    bets.push({
      stake: multiStake,
      correlationId: uuid(),
      stakeSource: isPromo ? "PROMOTION_USER" : "AVAILABLE",
      // Claimed independently of promo cash. The multi always carries
      // the key, explicit null when no token is attached.
      tokenIdAttemptedToClaim: multiToken,
      alternativeStakeReference: multiAlternativeStakeReference,
      selections: multiLegs.map((leg) => ({
        odds: leg.odds,
        outcomeIds: getSubmittedOutcomeIds(leg),
        correlationId: multiLegCorrelationIds[leg.outcomeId] ?? uuid(),
        attributes: {
          originType: leg.originType,
          originId: leg.originId,
        },
      })),
    });
  }

  // --- Combinations ---
  for (const [size, combo] of Object.entries(combinations)) {
    if (combo.total <= 0) continue;

    bets.push({
      stake: combo.total,
      correlationId: combo.correlationId || uuid(),
      stakeSource: combo.isPromo ? "PROMOTION_USER" : "AVAILABLE",
      selectionsRequiredToWin: Number(size),
      // Combo legs are bare odds + outcomeIds — legacy sends no per-leg
      // correlationId or attributes here.
      selections: multiLegs.map((leg) => ({
        odds: leg.odds,
        outcomeIds: getSubmittedOutcomeIds(leg),
      })),
    });
  }

  return {
    currency,
    acceptOddsChanges,
    bets,
    channel,
  };
};
